
import Vue from "vue";
import {
  downloadBucketEmailAttachment,
  getBucketEmailDetail,
  getBucketEmails
} from "@/api";
import {
  BucketEmailAttachment,
  BucketEmailDetail,
  BucketEmailListItem,
  BucketEmailMatch
} from "@/interfaces/BucketEmail.interfaces";

const DISPLAY_TIMEZONE = "America/Toronto";
type ListNavigation = "preserve" | "reset" | "next" | "previous";
interface ListRequest {
  cursor: string;
  search: string;
  from: string;
  to: string;
  pageSize: number;
  navigation: ListNavigation;
  previousCursor?: string;
  commitFilters: boolean;
}

export default Vue.extend({
  name: "BucketEmail",
  data() {
    return {
      messages: [] as BucketEmailListItem[],
      pageSize: 25,
      activePageSize: 25,
      currentCursor: "",
      nextCursor: "",
      cursorHistory: [] as string[],
      listLoading: false,
      listError: "",
      listRequestId: 0,
      failedListRequest: null as ListRequest | null,
      draftSearch: "",
      draftFrom: "",
      draftTo: "",
      activeSearch: "",
      activeFrom: "",
      activeTo: "",
      detailOpen: false,
      detailLoading: false,
      detailError: "",
      detailRequestId: 0,
      detail: null as BucketEmailDetail | null,
      selectedMessageId: null as string | null,
      downloadingAttachmentId: null as string | null,
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarColor: "error",
      headers: [
        { text: "Received", value: "receivedAt", width: "170" },
        { text: "From", value: "sender", sortable: false, width: "210" },
        { text: "To", value: "bucketRecipient", sortable: false, width: "190" },
        { text: "Subject / preview", value: "subject", sortable: false },
        {
          text: "TempStars match",
          value: "matches",
          sortable: false,
          width: "230"
        },
        {
          text: "Files",
          value: "attachmentCount",
          sortable: false,
          width: "80",
          align: "center"
        },
        { text: "", value: "actions", sortable: false, width: "80" }
      ]
    };
  },
  computed: {
    dateError(): string {
      return this.draftFrom && this.draftTo && this.draftFrom > this.draftTo
        ? "From date cannot be after To date."
        : "";
    },
    displayTimezoneLabel(): string {
      return "Toronto (ET)";
    },
    currentPageNumber(): number {
      return this.cursorHistory.length + 1;
    },
    canGoPrevious(): boolean {
      return this.cursorHistory.length > 0;
    },
    hasActiveFilters(): boolean {
      return Boolean(this.activeSearch || this.activeFrom || this.activeTo);
    },
    emptyStateTitle(): string {
      if (this.hasActiveFilters) return "No messages match these filters";
      return "No messages in Bucket Gmail";
    },
    emptyStateDetail(): string {
      if (this.hasActiveFilters)
        return "Clear or adjust the search and received dates.";
      return "The Bucket mailbox has no messages on this page.";
    },
    visibleAddresses(): BucketEmailDetail["addresses"] {
      return this.detail
        ? this.detail.addresses.filter(
            address => address.addressType !== "content"
          )
        : [];
    }
  },
  async mounted() {
    await this.loadMessages();
  },
  beforeDestroy() {
    this.listRequestId += 1;
    this.detailRequestId += 1;
  },
  watch: {
    detailOpen(isOpen: boolean) {
      if (isOpen) this.labelDetailDialog();
    }
  },
  methods: {
    labelDetailDialog() {
      // Vuetify 2 does not forward arbitrary attributes from <v-dialog> to
      // the actual role="dialog" wrapper. Label that generated element after
      // its lazy content has mounted so assistive technology gets a name.
      this.$nextTick(() => {
        this.$nextTick(() => {
          const dialog = this.$refs.detailDialog as Vue & {
            $refs?: { content?: HTMLElement };
          };
          const content = dialog && dialog.$refs && dialog.$refs.content;
          if (content) {
            content.setAttribute(
              "aria-labelledby",
              "bucket-email-detail-title"
            );
          }
        });
      });
    },
    showSnackbar(message: string, color = "error") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbarOpen = true;
    },
    async runListRequest(request: ListRequest): Promise<boolean> {
      const requestId = ++this.listRequestId;
      this.listLoading = true;
      this.listError = "";
      this.failedListRequest = null;
      try {
        const response = await getBucketEmails({
          query: request.search || undefined,
          from: request.from || undefined,
          to: request.to || undefined,
          pageSize: request.pageSize,
          cursor: request.cursor || undefined
        });
        if (requestId !== this.listRequestId) return false;
        this.messages = response.items;
        this.currentCursor = request.cursor;
        this.nextCursor = response.nextCursor || "";
        if (request.commitFilters) {
          this.activeSearch = request.search;
          this.activeFrom = request.from;
          this.activeTo = request.to;
          this.activePageSize = request.pageSize;
          this.pageSize = request.pageSize;
        }
        if (request.navigation === "reset") {
          this.cursorHistory = [];
        } else if (request.navigation === "next") {
          this.cursorHistory.push(request.previousCursor || "");
        } else if (request.navigation === "previous") {
          this.cursorHistory.pop();
        }
        return true;
      } catch (error) {
        if (requestId !== this.listRequestId) return false;
        this.failedListRequest = request;
        if (request.commitFilters) this.pageSize = this.activePageSize;
        this.listError =
          error instanceof Error
            ? error.message
            : "Failed to load Bucket Email";
        return false;
      } finally {
        if (requestId === this.listRequestId) this.listLoading = false;
      }
    },
    loadMessages(cursor?: string): Promise<boolean> {
      return this.runListRequest({
        cursor: cursor === undefined ? this.currentCursor : cursor,
        search: this.activeSearch,
        from: this.activeFrom,
        to: this.activeTo,
        pageSize: this.activePageSize,
        navigation: "preserve",
        commitFilters: false
      });
    },
    retryListLoad() {
      if (!this.failedListRequest) return;
      void this.runListRequest({ ...this.failedListRequest });
    },
    applyFilters() {
      if (this.dateError) return;
      void this.runListRequest({
        cursor: "",
        search: String(this.draftSearch || "").trim(),
        from: this.draftFrom,
        to: this.draftTo,
        pageSize: this.activePageSize,
        navigation: "reset",
        commitFilters: true
      });
    },
    clearFilters() {
      this.draftSearch = "";
      this.draftFrom = "";
      this.draftTo = "";
      void this.runListRequest({
        cursor: "",
        search: "",
        from: "",
        to: "",
        pageSize: this.activePageSize,
        navigation: "reset",
        commitFilters: true
      });
    },
    async refreshInbox() {
      await this.runListRequest({
        cursor: "",
        search: this.activeSearch,
        from: this.activeFrom,
        to: this.activeTo,
        pageSize: this.activePageSize,
        navigation: "reset",
        commitFilters: false
      });
    },
    async nextPage() {
      if (!this.nextCursor || this.listLoading) return;
      await this.runListRequest({
        cursor: this.nextCursor,
        search: this.activeSearch,
        from: this.activeFrom,
        to: this.activeTo,
        pageSize: this.activePageSize,
        navigation: "next",
        previousCursor: this.currentCursor,
        commitFilters: false
      });
    },
    async previousPage() {
      if (!this.cursorHistory.length || this.listLoading) return;
      await this.runListRequest({
        cursor: this.cursorHistory[this.cursorHistory.length - 1],
        search: this.activeSearch,
        from: this.activeFrom,
        to: this.activeTo,
        pageSize: this.activePageSize,
        navigation: "previous",
        commitFilters: false
      });
    },
    changePageSize() {
      void this.runListRequest({
        cursor: "",
        search: this.activeSearch,
        from: this.activeFrom,
        to: this.activeTo,
        pageSize: this.pageSize,
        navigation: "reset",
        commitFilters: true
      });
    },
    messageAriaLabel(item: BucketEmailListItem): string {
      return `Open message: ${item.subject || "(No subject)"}`;
    },
    async openMessage(item: BucketEmailListItem) {
      const requestId = ++this.detailRequestId;
      this.selectedMessageId = item.id;
      this.detailOpen = true;
      this.detail = null;
      this.detailError = "";
      this.detailLoading = true;
      try {
        const detail = await getBucketEmailDetail(item.id);
        if (requestId !== this.detailRequestId) return;
        this.detail = detail;
      } catch (error) {
        if (requestId !== this.detailRequestId) return;
        this.detailError =
          error instanceof Error
            ? error.message
            : "Failed to load this message";
      } finally {
        if (requestId === this.detailRequestId) this.detailLoading = false;
      }
    },
    retryDetail() {
      const item = this.messages.find(
        message => message.id === this.selectedMessageId
      );
      if (item) this.openMessage(item);
    },
    closeDetail() {
      this.detailRequestId += 1;
      this.detailOpen = false;
      this.detailLoading = false;
    },
    async downloadAttachment(attachment: BucketEmailAttachment) {
      if (!this.detail || !attachment.downloadable || !attachment.id) return;
      this.downloadingAttachmentId = attachment.id;
      try {
        const blob = await downloadBucketEmailAttachment(
          this.detail.id,
          attachment.id
        );
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = attachment.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.showSnackbar(
          error instanceof Error ? error.message : "Attachment download failed"
        );
      } finally {
        this.downloadingAttachmentId = null;
      }
    },
    formatDate(value: string): string {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "—";
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: DISPLAY_TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short"
      }).format(date);
    },
    formatBytes(value: number): string {
      if (!value) return "0 B";
      if (value < 1024) return `${value} B`;
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${(value / (1024 * 1024)).toFixed(1)} MB`;
    },
    addressLabel(type: string): string {
      const labels: Record<string, string> = {
        sender: "From",
        to: "To",
        cc: "Cc",
        bcc: "Bcc",
        reply_to: "Reply-To"
      };
      return labels[type] || type;
    },
    formatAddress(address: {
      displayName: string | null;
      email: string;
    }): string {
      return address.displayName
        ? `${address.displayName} (${address.email})`
        : address.email;
    },
    matchKey(match: BucketEmailMatch): string {
      return [
        match.type,
        match.userId,
        match.hygienistId,
        match.locationId,
        match.dentistId
      ].join("-");
    },
    matchLabel(match: BucketEmailMatch): string {
      if (match.ambiguous) return "Ambiguous match";
      if (match.type === "talent")
        return `${match.displayName || "Talent"} · H${match.hygienistId}`;
      return `${match.practiceName || "Practice"} · L${match.locationId}`;
    },
    attachmentStatus(status: string): string {
      const labels: Record<string, string> = {
        oversized: "Too large to download",
        unsupported: "Unsupported file type",
        unavailable: "Content unavailable"
      };
      return labels[status] || "Unavailable";
    }
  }
});
